import FetchService from "@core-ui/ApiServices/FetchService";
import ApiUrlCreatorService from "@core-ui/ContextServices/ApiUrlCreator";
import WorkspaceService from "@core-ui/ContextServices/Workspace";
import { clearData } from "@core-ui/utils/initGlobalFuncs";
import { useWorkspaceLogo } from "@ext/workspace/components/useWorkspaceLogo";
import { useWorkspaceStyle } from "@ext/workspace/components/useWorkspaceStyle";
import type { ClientWorkspaceConfig } from "@ext/workspace/WorkspaceConfig";
import { useCallback, useState } from "react";

export const useWorkspaceEditorActions = (initialWorkspace: ClientWorkspaceConfig) => {
	const apiUrlCreator = ApiUrlCreatorService.value;
	const workspaces = WorkspaceService.workspaces();
	const pathPlaceholder = WorkspaceService.defaultPath();

	const [open, setOpenInner] = useState(true);
	const [deleteInProgress, setDeleteInProgress] = useState(false);
	const {
		confirmChanges: confirmLogo,
		haveChanges: haveLogoChanges,
		...workspaceLogoProps
	} = useWorkspaceLogo(initialWorkspace?.path);

	const {
		confirmChanges: confirmStyle,
		haveChanges: haveStyleChanges,
		...workspaceStyleProps
	} = useWorkspaceStyle(initialWorkspace?.path);

	const [originalProps, setOriginalProps] = useState<ClientWorkspaceConfig>({ ...initialWorkspace });

	// biome-ignore lint/correctness/useExhaustiveDependencies: needed
	const setOpen = useCallback(
		(v: boolean) => {
			if (v) setOriginalProps({ ...initialWorkspace });
			setOpenInner(v);
		},
		[initialWorkspace, pathPlaceholder, workspaces],
	);

	const removeWorkspace = async (closeCallback: () => void) => {
		setDeleteInProgress(true);
		clearData();
		await FetchService.fetch(apiUrlCreator.removeWorkspace(originalProps.path));
		// Close the modal before refreshing: the refresh reloads page data and
		// switches the app to the next workspace, which would otherwise re-render
		// the still-open modal on that workspace instead of dismissing it.
		closeCallback();
		await refreshPage();
	};

	const onSubmit = async (
		newProps: ClientWorkspaceConfig & { ai?: { token: string } },
		closeCallback: () => void,
	) => {
		if (deleteInProgress) return;

		const { settings: _settings, ...workspaceProps } = newProps;

		clearData();
		await FetchService.fetch(apiUrlCreator.editWorkspace(), JSON.stringify(workspaceProps));
		await confirmLogo();
		await confirmStyle();
		await refreshPage();
		closeCallback();
		setOpenInner(false);
	};

	return {
		open,
		setOpen,
		originalProps,
		workspaces,
		pathPlaceholder,
		deleteInProgress,
		removeWorkspace,
		onSubmit,
		workspaceLogoProps,
		workspaceStyleProps,
		haveLogoChanges,
		haveStyleChanges,
	};
};
